import {
  Button,
  Col,
  Divider,
  Form,
  InputNumber,
  message,
  Pagination,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { ColumnsType } from "antd/lib/table";
import { IAssignedItem, IEligibleVendor, IPurchaseIndent } from "common/interface/IPurchaseIndent";
import { routes } from "common/routes";
import { AppPageHeader } from "components/AppPageHeader";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { useDocumentTitle } from "hooks/usePageTitle";
import { useCallback, useEffect, useMemo, useState } from "react";
import { BsPlus } from "react-icons/bs";
import { MdCheck, MdClose, MdDeleteOutline, MdEdit } from "react-icons/md";
import { useNavigate, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import {
  assignVendorToIndent,
  clearEligibleVendors,
  getAllPurchaseIndents,
  getEligibleVendors,
  removeVendorFromIndent,
  updatePurchaseIndentQty,
} from "store/purchase-indent/purchaseIndentSlice";

const { Option } = Select;
const MESSAGE_KEY = "purchase_indents";

type PartialIndentType = Partial<IPurchaseIndent> & { key: string };

const StyledPagination = styled(Pagination)`
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
`;

const VendorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 8px;
  border-radius: 6px;
  background: #fafafa;
  border: 1px solid #f0f0f0;
  &:hover {
    border-color: #d9d9d9;
    background: #fff;
  }
`;

const VendorInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const MetaLabel = styled.span`
  font-size: 11px;
  color: #8c8c8c;
  margin-right: 2px;
`;

const MetaValue = styled.span`
  font-size: 12px;
  color: #434343;
`;

const statusColor: Record<string, string> = {
  OPEN: "green",
  CLOSED: "red",
  PARTIAL: "orange",
};

// ─── Single assigned vendor row ──────────────────────────────────────────────

interface VendorItemRowProps {
  item: IAssignedItem;
  indentId: number;
  isSaving: boolean;
  eligibleVendors: IEligibleVendor[];
}

const VendorItemRow: React.FC<VendorItemRowProps> = ({ item, indentId, isSaving, eligibleVendors }) => {
  const dispatch = useAppDispatch();
  const resolvedName =
    item.vendorName ??
    eligibleVendors.find((v) => v.vendorId === item.vendorId)?.vendorName;
  const [isEditing, setIsEditing] = useState(false);
  const [form] = Form.useForm();

  const startEdit = () => {
    form.setFieldsValue({ qty: item.requestedQty, poRate: item.poId });
    setIsEditing(true);
  };

  const cancelEdit = () => {
    form.resetFields();
    setIsEditing(false);
  };

  const onSave = async () => {
    try {
      const values = await form.validateFields();
      dispatch(
        assignVendorToIndent({
          id: indentId,
          vendorId: item.vendorId,
          qty: values.qty,
          poRate: values.poRate,
        })
      )
        .unwrap()
        .then(() => {
          setIsEditing(false);
          message.success("Vendor updated.");
        })
        .catch((err: any) => message.error(err?.message || "Failed to update.", 4));
    } catch (_) {}
  };

  const onDelete = () => {
    dispatch(removeVendorFromIndent({ id: indentId, vendorId: item.vendorId }))
      .unwrap()
      .then(() => message.success("Vendor removed."))
      .catch((err: any) => message.error(err?.message || "Failed to remove.", 4));
  };

  if (isEditing) {
    return (
      <VendorRow style={{ flexDirection: "column", alignItems: "flex-start" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", marginBottom: 6 }}>
          <div>
            <Typography.Text strong style={{ fontSize: 12 }}>
              {item.vendorId}
            </Typography.Text>
            {resolvedName && (
              <Typography.Text style={{ fontSize: 12, marginLeft: 6 }}>
                {resolvedName}
              </Typography.Text>
            )}
          </div>
          <Space size={4}>
            <Tooltip title="Save">
              <Button
                type="primary"
                size="small"
                shape="circle"
                icon={<MdCheck size={13} style={{ marginTop: 1 }} />}
                onClick={onSave}
                loading={isSaving}
              />
            </Tooltip>
            <Tooltip title="Cancel">
              <Button
                size="small"
                shape="circle"
                icon={<MdClose size={13} style={{ marginTop: 1 }} />}
                onClick={cancelEdit}
                disabled={isSaving}
              />
            </Tooltip>
            <Popconfirm
              title="Remove this vendor?"
              onConfirm={onDelete}
              disabled={isSaving}
              okText="Remove"
              okType="danger"
            >
              <Tooltip title="Remove">
                <Button
                  size="small"
                  shape="circle"
                  danger
                  type="text"
                  icon={<MdDeleteOutline size={14} style={{ marginTop: 2 }} />}
                  disabled={isSaving}
                />
              </Tooltip>
            </Popconfirm>
          </Space>
        </div>
        <Form form={form} component={false}>
          <Space size={6} wrap={false}>
            <Form.Item
              name="qty"
              style={{ margin: 0 }}
              rules={[
                { required: true, message: "" },
                { validator: (_, v) => (v > 0 ? Promise.resolve() : Promise.reject()) },
              ]}
            >
              <InputNumber
                placeholder="Quantity"
                size="small"
                min={0.01}
                step={0.01}
                style={{ width: 100 }}
                addonBefore={<span style={{ fontSize: 10, color: "#8c8c8c" }}>Qty</span>}
              />
            </Form.Item>
            <Form.Item
              name="poRate"
              style={{ margin: 0 }}
              rules={[
                { required: true, message: "" },
                { validator: (_, v) => (v > 0 ? Promise.resolve() : Promise.reject()) },
              ]}
            >
              <InputNumber
                placeholder="Rate"
                size="small"
                min={0.01}
                step={0.01}
                style={{ width: 110 }}
                addonBefore={<span style={{ fontSize: 10, color: "#8c8c8c" }}>PO Rate</span>}
              />
            </Form.Item>
          </Space>
        </Form>
      </VendorRow>
    );
  }

  return (
    <VendorRow>
      <VendorInfo>
        <div style={{ fontSize: 10, color: "#8c8c8c", marginBottom: 2 }}>
          PO #{item.poId}
        </div>
        <div>
          <Typography.Text strong style={{ fontSize: 12 }}>
            {item.vendorId}
          </Typography.Text>
          {resolvedName && (
            <Typography.Text style={{ fontSize: 12, marginLeft: 6 }}>
              {resolvedName}
            </Typography.Text>
          )}
        </div>
        <div style={{ marginTop: 2 }}>
          <MetaLabel>Qty</MetaLabel>
          <MetaValue>{item.requestedQty}</MetaValue>
        </div>
      </VendorInfo>
      <Tooltip title="Edit quantity / PO rate">
        <Button
          type="text"
          size="small"
          shape="circle"
          icon={<MdEdit size={14} style={{ marginTop: 2 }} />}
          onClick={startEdit}
          disabled={isSaving}
        />
      </Tooltip>
      <Popconfirm
        title="Remove this vendor?"
        onConfirm={onDelete}
        disabled={isSaving}
        okText="Remove"
        okType="danger"
      >
        <Tooltip title="Remove">
          <Button
            type="text"
            size="small"
            shape="circle"
            danger
            icon={<MdDeleteOutline size={14} style={{ marginTop: 2 }} />}
            disabled={isSaving}
          />
        </Tooltip>
      </Popconfirm>
    </VendorRow>
  );
};

// ─── Add new vendor section ───────────────────────────────────────────────────

interface NewVendorSectionProps {
  indentId: number;
  eligibleVendors: IEligibleVendor[];
  assignedVendorIds: number[];
  isSaving: boolean;
  isLoadingVendors: boolean;
}

const NewVendorSection: React.FC<NewVendorSectionProps> = ({
  indentId,
  eligibleVendors,
  assignedVendorIds,
  isSaving,
  isLoadingVendors,
}) => {
  const dispatch = useAppDispatch();
  const [selectedVendorId, setSelectedVendorId] = useState<number | null>(null);
  const [form] = Form.useForm();

  const availableVendors = useMemo(
    () =>
      eligibleVendors
        .filter((v, i, self) => i === self.findIndex((t) => t.vendorId === v.vendorId))
        .filter((v) => !assignedVendorIds.includes(v.vendorId)),
    [eligibleVendors, assignedVendorIds]
  );

  const cancel = () => {
    setSelectedVendorId(null);
    form.resetFields();
  };

  const onAdd = async () => {
    if (!selectedVendorId) return;
    try {
      const values = await form.validateFields();
      dispatch(
        assignVendorToIndent({
          id: indentId,
          vendorId: selectedVendorId,
          qty: values.qty,
          poRate: values.poRate,
        })
      )
        .unwrap()
        .then(() => {
          cancel();
          message.success("Vendor assigned.");
        })
        .catch((err: any) => message.error(err?.message || "Failed to assign.", 4));
    } catch (_) {}
  };

  return (
    <div style={{ marginTop: 8 }}>
      <Divider style={{ margin: "6px 0" }} />
      <Select
        showSearch
        allowClear
        size="small"
        placeholder={
          <span>
            <BsPlus style={{ verticalAlign: "middle", marginRight: 4 }} />
            Add vendor
          </span>
        }
        value={selectedVendorId ?? undefined}
        onChange={(v) => setSelectedVendorId(v ?? null)}
        onClear={cancel}
        style={{ width: "100%" }}
        optionFilterProp="label"
        loading={isLoadingVendors}
        notFoundContent={isLoadingVendors ? "Loading..." : "No available vendors"}
        disabled={isSaving}
      >
        {availableVendors.map((v) => (
          <Option
            key={v.vendorId}
            value={v.vendorId}
            label={v.vendorName ? `${v.vendorId} · ${v.vendorName}` : `Vendor ${v.vendorId}`}
          >
            <div>
              <div>
                <Typography.Text strong style={{ fontSize: 12 }}>
                  {v.vendorId} : 
                  {v.vendorName && (
                    <span style={{ fontWeight: 400, marginLeft: 6 }}>{v.vendorName}</span>
                  )}
                </Typography.Text>
              </div>
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                Score: {v.reliabilityScore}
              </Typography.Text>
            </div>
          </Option>
        ))}
      </Select>

      {selectedVendorId !== null && (
        <Form form={form} component={false}>
          <Row gutter={[6, 0]} align="middle" style={{ marginTop: 8 }} wrap={false}>
            <Col>
              <Form.Item
                name="qty"
                style={{ margin: 0 }}
                rules={[
                  { required: true, message: "" },
                  { validator: (_, v) => (v > 0 ? Promise.resolve() : Promise.reject("Must be > 0")) },
                ]}
              >
                <InputNumber
                  placeholder="Qty"
                  size="small"
                  min={0.01}
                  step={0.01}
                  style={{ width: 80 }}
                />
              </Form.Item>
            </Col>
            <Col>
              <Form.Item
                name="poRate"
                style={{ margin: 0 }}
                rules={[
                  { required: true, message: "" },
                  { validator: (_, v) => (v > 0 ? Promise.resolve() : Promise.reject("Must be > 0")) },
                ]}
              >
                <InputNumber
                  placeholder="PO Rate"
                  size="small"
                  min={0.01}
                  step={0.01}
                  style={{ width: 90 }}
                />
              </Form.Item>
            </Col>
            <Col>
              <Button
                type="primary"
                size="small"
                onClick={onAdd}
                loading={isSaving}
              >
                Add
              </Button>
            </Col>
            <Col>
              <Button size="small" onClick={cancel} disabled={isSaving}>
                Cancel
              </Button>
            </Col>
          </Row>
        </Form>
      )}
    </div>
  );
};

// ─── Vendors column — edit mode ───────────────────────────────────────────────

interface VendorEditSectionProps {
  indent: IPurchaseIndent;
  eligibleVendors: IEligibleVendor[];
  status: string;
}

const VendorEditSection: React.FC<VendorEditSectionProps> = ({
  indent,
  eligibleVendors,
  status,
}) => {
  const isSaving = status === "saving";
  const isLoadingVendors = status === "loading";
  const assignedItems = indent.assignedItems ?? [];
  const assignedVendorIds = assignedItems.map((i) => i.vendorId);

  return (
    <div style={{ minWidth: 300, maxWidth: 460 }}>
      {assignedItems.length > 0 ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {assignedItems.map((item) => (
            <VendorItemRow
              key={item.itemId}
              item={item}
              indentId={indent.id}
              isSaving={isSaving}
              eligibleVendors={eligibleVendors}
            />
          ))}
        </div>
      ) : (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          No vendors assigned yet.
        </Typography.Text>
      )}
      <NewVendorSection
        indentId={indent.id}
        eligibleVendors={eligibleVendors}
        assignedVendorIds={assignedVendorIds}
        isSaving={isSaving}
        isLoadingVendors={isLoadingVendors}
      />
    </div>
  );
};

// ─── Vendors column — display mode ───────────────────────────────────────────

const VendorDisplaySection: React.FC<{ assignedItems: IAssignedItem[] | null }> = ({
  assignedItems,
}) => {
  if (!assignedItems || assignedItems.length === 0) {
    return <Typography.Text type="secondary">No Assigned Vendors</Typography.Text>;
  }
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
      {assignedItems.map((item) => (
        <Tag key={item.itemId} style={{ margin: 0, padding: "4px 8px", lineHeight: 1 }}>
          <div style={{ fontSize: 10, color: "#8c8c8c", marginBottom: 3 }}>
            PO #{item.poId}
          </div>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 2 }}>
            {item.vendorId}
            {item.vendorName && (
              <span style={{ fontWeight: 400, marginLeft: 5 }}>{item.vendorName}</span>
            )}
          </div>
          <div style={{ fontSize: 11, color: "#595959" }}>
            Qty: {item.requestedQty}
          </div>
        </Tag>
      ))}
    </div>
  );
};

// ─── Listing page ─────────────────────────────────────────────────────────────

const PurchaseIndentListingPage = () => {
  useDocumentTitle("Purchase Indents");
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { indents, eligibleVendors, status, error } = useAppSelector(
    (state) => state.purchaseIndent
  );
  const [searchParams] = useSearchParams();
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editQtyValue, setEditQtyValue] = useState<number>(0);

  const getData = useCallback(
    (pageNum = 1, pageSize = 25, filter = "") => {
      dispatch(getAllPurchaseIndents({ pageNo: pageNum, pageSize, filter }));
    },
    [dispatch]
  );

  useEffect(() => {
    getData(1, 25, searchParams.toString());
  }, [getData, searchParams]);

  useEffect(() => {
    if (status === "error") {
      message.error({ content: error, key: MESSAGE_KEY });
    } else {
      message.destroy(MESSAGE_KEY);
    }
  }, [status, error]);

  const handleStartEdit = useCallback(
    (indent: IPurchaseIndent) => {
      setEditingId(indent.id);
      setEditQtyValue(indent.qty);
      dispatch(getEligibleVendors({ purchaseSkuCode: indent.purchaseSkuCode }));
    },
    [dispatch]
  );

  const handleEndEdit = useCallback(() => {
    setEditingId(null);
    setEditQtyValue(0);
    dispatch(clearEligibleVendors());
  }, [dispatch]);

  const handleSave = useCallback(() => {
    if (!editingId) return;
    const original = indents?.data.find((i) => i.id === editingId);
    if (!original) { handleEndEdit(); return; }

    if (editQtyValue === original.qty) {
      handleEndEdit();
      return;
    }

    dispatch(updatePurchaseIndentQty({ id: editingId, qty: editQtyValue }))
      .unwrap()
      .then(() => {
        message.success("Quantity updated.");
        handleEndEdit();
      })
      .catch((err: any) => message.error(err?.message || "Failed to update quantity.", 4));
  }, [dispatch, editingId, editQtyValue, indents, handleEndEdit]);

  const rows: PartialIndentType[] = useMemo(
    () => (indents?.data ?? []).map((indent, i) => ({ ...indent, key: `indent-${i}` })),
    [indents]
  );

  const columns: ColumnsType<PartialIndentType> = useMemo(
    () => [
      {
        key: "id",
        title: "Indent ID",
        dataIndex: "id",
        width: 90,
      },
      {
        key: "purchaseSkuCode",
        title: "SKU Code",
        dataIndex: "purchaseSkuCode",
        width: 130,
      },
      {
        key: "purchaseSkuName",
        title: "SKU Name",
        dataIndex: "purchaseSkuName",
        width: 160,
      },
      {
        key: "qty",
        title: "Total Qty",
        dataIndex: "qty",
        width: 110,
        render: (_: any, record: PartialIndentType) => {
          if (editingId === record.id) {
            return (
              <InputNumber
                value={editQtyValue}
                onChange={(v) => setEditQtyValue(v ?? 0)}
                min={0.01}
                step={0.01}
                size="small"
                style={{ width: 90 }}
                disabled={status === "saving"}
              />
            );
          }
          return <>{record.qty}</>;
        },
      },
      {
        key: "orderDate",
        title: "Order Date",
        dataIndex: "orderDate",
        width: 110,
      },
      {
        key: "status",
        title: "Status",
        dataIndex: "status",
        width: 90,
        render: (_: any, record: PartialIndentType) => (
          <Tag color={statusColor[record.status ?? ""] ?? "default"} style={{ margin: 0 }}>
            {record.status}
          </Tag>
        ),
      },
      {
        key: "assignedQty",
        title: "Assigned Qty",
        dataIndex: "assignedQty",
        width: 100,
      },
      {
        key: "remainingQty",
        title: "Remaining Qty",
        dataIndex: "remainingQty",
        width: 110,
      },
      {
        key: "vendors",
        title: "Vendors",
        dataIndex: "vendors",
        render: (_: any, record: PartialIndentType) => {
          const indent = record as IPurchaseIndent;
          if (editingId === indent.id) {
            return (
              <VendorEditSection
                indent={indent}
                eligibleVendors={eligibleVendors}
                status={status}
              />
            );
          }
          return <VendorDisplaySection assignedItems={indent.assignedItems} />;
        },
      },
      {
        key: "actions",
        title: "Actions",
        width: 110,
        render: (_: any, record: PartialIndentType) => {
          const indent = record as IPurchaseIndent;
          const isEditing = editingId === indent.id;
          const isSaving = status === "saving";

          if (isEditing) {
            return (
              <Space size={4} direction="vertical">
                <Button
                  type="primary"
                  size="small"
                  icon={<MdCheck size={13} style={{ marginTop: 1 }} />}
                  onClick={handleSave}
                  loading={isSaving}
                  style={{ width: 90 }}
                >
                  Save
                </Button>
                <Button
                  size="small"
                  icon={<MdClose size={13} style={{ marginTop: 1 }} />}
                  onClick={handleEndEdit}
                  disabled={isSaving}
                  style={{ width: 90 }}
                >
                  Cancel
                </Button>
              </Space>
            );
          }

          return (
            <Tooltip title="Edit indent">
              <Button
                type="text"
                size="small"
                shape="circle"
                icon={<MdEdit size={15} style={{ marginTop: 2 }} />}
                onClick={() => handleStartEdit(indent)}
                disabled={!!editingId}
              />
            </Tooltip>
          );
        },
      },
    ],
    [editingId, editQtyValue, eligibleVendors, status, handleStartEdit, handleEndEdit, handleSave]
  );

  return (
    <>
      <Row>
        <Col>
          <AppPageHeader
            title="Purchase Indents"
            extra={[
              <Button
                key="create"
                type="primary"
                onClick={() => navigate(`/${routes.PURCHASE_INDENT.CREATE}`)}
              >
                Create New Indent
              </Button>,
            ]}
          />
        </Col>
      </Row>

      {rows.length > 0 ? (
        <Row style={{ marginTop: "1rem" }}>
          <Col xs={24}>
            <Table
              columns={columns}
              dataSource={rows}
              scroll={{ x: "max-content", y: "65vh" }}
              size="small"
              pagination={false}
              loading={status === "loading" && !editingId}
              bordered
            />
          </Col>
          <Col xs={24}>
            {indents && (
              <StyledPagination
                current={indents.pageNo}
                pageSize={indents.pageSize}
                total={indents.total}
                onChange={(page, size) => {
                  if (editingId) handleEndEdit();
                  getData(page, size, searchParams.toString());
                }}
              />
            )}
          </Col>
        </Row>
      ) : (
        status !== "loading" && (
          <Row style={{ marginTop: "1rem" }}>No Purchase Indents to show.</Row>
        )
      )}
    </>
  );
};

export default PurchaseIndentListingPage;
